import { Typography } from '../../components/typography/typography';
import { Button } from '../../components/form/button';
import React, { useEffect, useState } from 'react';
import { Card } from '../../components/card/card';
import { Spinner } from '../../components/spinner/spinner';
import { Admin } from '../../clients/quipli-service/admin/admin';
import { useAccountingApiV1 } from '../../clients/quipli-service/accounting/accounting-api-v1';
import { AccountingPeriodClose } from '../../clients/quipli-service/accounting/accounting';
import { toast } from '../../util/toasts';
import { Modal } from '../../components/modal/modal';
import { DateTime, Interval } from 'luxon';
import { DatePicker } from '../../components/date-picker/date-picker';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { useAdminApiV1 } from '../../clients/quipli-service/admin/admin-api-v1';
import Icon from '../../components/icons/icon';
import { ConfirmModal } from '../../components/modal/confirm-modal';

export const AccountingPeriodCloseView = () => {
  const { getConfiguration } = useAPI();
  const { getAdminsList } = useAdminApiV1();
  const { listAccountingPeriodCloses, deleteAccountingPeriodClose, getCurrentAccountingPeriodClose } =
    useAccountingApiV1();
  const [accountPeriodCloses, setAccountPeriodCloses] = useState<AccountingPeriodClose[]>();
  const [adminUsers, setAdminUsers] = useState<Admin[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [timezone, setTimezone] = useState<string>();
  const [toDelete, setToDelete] = useState<AccountingPeriodClose | undefined>();
  const [currentAccountingPeriodClose, setCurrentAccountingPeriodClose] = useState<AccountingPeriodClose | undefined>();

  async function fetchReport() {
    try {
      setIsLoading(true);
      const [accountingPeriodCloses, adminUsers, timezoneConf, currentAccountingPeriodClose] = await Promise.all([
        listAccountingPeriodCloses(),
        getAdminsList(),
        getConfiguration('CONF_TIMEZONE'),
        getCurrentAccountingPeriodClose(),
      ]);
      setAccountPeriodCloses(accountingPeriodCloses);
      setAdminUsers(adminUsers);
      setTimezone(timezoneConf?.value);
      setCurrentAccountingPeriodClose(currentAccountingPeriodClose);
    } catch (e: any) {
      toast('Error loading accounting period closes', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchReport();
  }, []);

  return (
    <div className="accounting container-v2 flex flex-column gap-2">
      <div className="flex justify-between items-center">
        <Typography type="display-sm" weight="medium">
          Accounting Periods
        </Typography>
        <div className="flex gap-2">
          <Button className="button-yellow font-semibold" onClick={() => setIsCreateModalOpen(true)}>
            Close Accounting Period
          </Button>
        </div>
      </div>

      <Card>
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left">Close Date</th>
              <th className="text-left">Closed At</th>
              <th className="text-left">Closed By</th>
              <th className="text-left">Deleted At</th>
              <th className="text-left">Deleted By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={100}>
                  <Spinner />
                </td>
              </tr>
            ) : (
              <>
                {accountPeriodCloses && (
                  <>
                    {accountPeriodCloses.length > 0 ? (
                      accountPeriodCloses.map((accountPeriodClose, i) => {
                        return (
                          <tr key={i}>
                            <td>{accountPeriodClose.closeDateTime.toFormat('D')}</td>
                            <td>{accountPeriodClose.createdAt.toFormat('D t')}</td>
                            <td>
                              {accountPeriodClose.createdBy &&
                                adminUsers?.find((au) => au.adminId === accountPeriodClose.createdBy)?.adminEmail}
                            </td>
                            <td>{accountPeriodClose.deletedAt?.toFormat('D t')}</td>
                            <td>
                              {accountPeriodClose.deletedBy &&
                                adminUsers?.find((au) => au.adminId === accountPeriodClose.deletedBy)?.adminEmail}
                            </td>
                            <td>
                              {!accountPeriodClose.deletedAt && (
                                <Button
                                  className="button-tertiary account-button"
                                  onClick={() => setToDelete(accountPeriodClose)}
                                >
                                  <Icon icon={'Trash'} stroke="var(--grey-400)" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5}>No records found</td>
                      </tr>
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </Card>
      {isCreateModalOpen && timezone && (
        <CreateAccountingPeriodCloseModal
          close={async (didCreate) => {
            setIsCreateModalOpen(false);
            if (didCreate) {
              await fetchReport();
            }
          }}
          timezone={timezone}
          currentAccountingPeriodClose={currentAccountingPeriodClose}
        />
      )}
      {toDelete && (
        <ConfirmModal
          isOpen={true}
          close={() => setToDelete(undefined)}
          onConfirm={async () => {
            try {
              await deleteAccountingPeriodClose(toDelete.id);
              await fetchReport();
              setToDelete(undefined);
            } catch (e: any) {
              if (e.status === 400) {
                toast(e.message, 'error');
              } else {
                toast('Error deleting account period close', 'error');
              }
            }
          }}
          prompt={`Are you sure you want to delete account period close ${toDelete.closeDateTime.toFormat('D')}?`}
        />
      )}
    </div>
  );
};

const CreateAccountingPeriodCloseModal = ({
  close,
  timezone,
  currentAccountingPeriodClose,
}: {
  close: (didCreate: boolean) => void;
  timezone: string;
  currentAccountingPeriodClose?: AccountingPeriodClose;
}) => {
  const { createAccountingPeriodClose } = useAccountingApiV1();
  const [closeDateTime, setCloseDateTime] = useState<DateTime>(
    DateTime.local({ zone: timezone }).minus({ days: 1 }).endOf('day'),
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  async function doSave() {
    try {
      setIsSaving(true);
      closeDateTime && (await createAccountingPeriodClose({ closeDateTime }));
      close(true);
    } catch (e) {
      console.log(e);
      toast(`Error saving accounting period close`, 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      title="Close Accounting Period"
      isOpen={true}
      close={() => close(false)}
      overflow="visible"
      actions={
        <>
          <Button
            className="button button-secondary"
            disabled={isSaving || !closeDateTime}
            onClick={() => close(false)}
          >
            Cancel
          </Button>
          <Button className="button button-yellow" disabled={isSaving} onClick={doSave}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-column gap-2">
        <div className="flex items-center gap-2">
          <label className="basis-1/2">Close Date</label>
          <DatePicker
            value={closeDateTime}
            onChange={(dt) => dt && setCloseDateTime(dt.endOf('day'))}
            disabledIntervals={
              currentAccountingPeriodClose && [
                Interval.fromDateTimes(
                  DateTime.fromMillis(0),
                  currentAccountingPeriodClose.closeDateTime.plus({ days: 1 }),
                ),
              ]
            }
          />
        </div>
      </div>
    </Modal>
  );
};
